/**
 * UserTable Component
 * Renders the users table with sorting, pagination, selection, and visible action buttons.
 * Extracted from UsersManagement to keep files under 500 lines.
 */

import { Check, X, Mail, Eye, Power, Shield, UserCheck, Building2, ChevronLeft, ChevronRight, Trash2, LogIn, MoreVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { TooltipProvider } from '../../components/ui/tooltip';
import { UserProfile } from '../../lib/api';
import { canSendInvitation } from './userInvitation';
import { RoleBadges } from './RoleBadges';
import { PrimaryContactCell, InvitationBadge } from './UserTableCells';
import { departmentLabel } from '../../lib/departments';
import { useFormatters } from '../../contexts/PreferencesContext';

export type SortField = 'name' | 'email' | 'company' | 'department' | 'role' | 'created_at' | 'last_login';
export type SortDirection = 'asc' | 'desc';

interface UserTableProps {
  /** Paginated list of users to display */
  users: UserProfile[];
  /** Shown when `users` is empty; defaults to "No users found". */
  emptyMessage?: string;
  /** Set of selected user IDs */
  selectedUsers: Set<number>;
  /** Toggle selection for a single user */
  onToggleSelection: (id: number) => void;
  /** Toggle select-all for current page */
  onToggleSelectAll: () => void;
  /** Open user details modal */
  onViewDetails: (user: UserProfile) => void;
  /** Toggle user active/inactive */
  onToggleActive: (user: UserProfile) => void;
  /** Open change role dialog */
  onChangeRole: (user: UserProfile) => void;
  /** Platform staff only: open the change-company (relink) dialog. */
  onChangeCompany?: (user: UserProfile) => void;
  /** Send invitation email to user */
  onSendInvitation: (user: UserProfile) => void;
  /** Approve a pending user */
  onApprove: (userId: number) => void;
  /** CR-5312: open the delete-or-deactivate confirm flow for a user. */
  onSafeDelete: (user: UserProfile) => void;
  /** View activity log for a user */
  onViewActivity: (user: UserProfile) => void;
  /** CR-6273: whether the logged-in admin may impersonate at all (drives the
   *  per-row "Login as" button; the per-user eligibility is checked inline). */
  canImpersonate: boolean;
  /** CR-6273: impersonate the given user (scoped to the active company view). */
  onLoginAs: (user: UserProfile) => void;
  /** Check if user is the current logged-in user */
  isSelf: (user: UserProfile) => boolean;
  /** Table is loading data */
  loading: boolean;
  /** An action is in progress */
  actionLoading: boolean;
  /** Current sort field */
  sortField: SortField;
  /** Current sort direction */
  sortDirection: SortDirection;
  /** Handle sort column click */
  onSort: (field: SortField) => void;
  /** Current page number */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total filtered count (before pagination) */
  totalFiltered: number;
  /** Handle page change */
  onPageChange: (page: number) => void;
}

function SortIndicator({ field, sortField, sortDirection }: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
}) {
  if (sortField !== field) return null;
  return <span className="ml-1">{sortDirection === 'asc' ? '\u2191' : '\u2193'}</span>;
}

/**
 * User management table with inline action buttons (VIC-style).
 * Displays user data with sorting, selection checkboxes, and per-row visible actions.
 */
export default function UserTable({
  users,
  emptyMessage = 'No users found',
  selectedUsers,
  onToggleSelection,
  onToggleSelectAll,
  onViewDetails,
  onToggleActive,
  onChangeRole,
  onChangeCompany,
  onSendInvitation,
  onApprove,
  onSafeDelete,
  onViewActivity,
  canImpersonate,
  onLoginAs,
  isSelf,
  loading,
  actionLoading,
  sortField,
  sortDirection,
  onSort,
  currentPage,
  totalPages,
  totalFiltered,
  onPageChange,
}: UserTableProps) {
  const sortableHead = (label: string, field: SortField) => (
    <TableHead
      className="cursor-pointer hover:bg-muted/50 select-none"
      onClick={() => onSort(field)}
    >
      {label}
      <SortIndicator field={field} sortField={sortField} sortDirection={sortDirection} />
    </TableHead>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <div className="border rounded-lg">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={
                      users.length > 0 && users.every((u) => selectedUsers.has(u.id))
                    }
                    onCheckedChange={onToggleSelectAll}
                  />
                </TableHead>
                {sortableHead('Email', 'email')}
                {sortableHead('Name', 'name')}
                {sortableHead('Company', 'company')}
                {sortableHead('Department', 'department')}
                <TableHead>Primary Contact</TableHead>
                {sortableHead('User Role', 'role')}
                {sortableHead('Last login', 'last_login')}
                <TableHead>Status</TableHead>
                <TableHead>Invitation</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((userProfile) => (
                  <UserTableRow
                    key={userProfile.id}
                    userProfile={userProfile}
                    selected={selectedUsers.has(userProfile.id)}
                    onToggleSelection={onToggleSelection}
                    onViewDetails={onViewDetails}
                    onToggleActive={onToggleActive}
                    onChangeRole={onChangeRole}
                    onChangeCompany={onChangeCompany}
                    onSendInvitation={onSendInvitation}
                    onApprove={onApprove}
                    onSafeDelete={onSafeDelete}
                    onViewActivity={onViewActivity}
                    canImpersonate={canImpersonate}
                    onLoginAs={onLoginAs}
                    isSelf={isSelf(userProfile)}
                    actionLoading={actionLoading}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-6 py-3">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages} ({totalFiltered} users)
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

// --- Individual Row Component ---

interface UserTableRowProps {
  userProfile: UserProfile;
  selected: boolean;
  onToggleSelection: (id: number) => void;
  onViewDetails: (user: UserProfile) => void;
  onToggleActive: (user: UserProfile) => void;
  onChangeRole: (user: UserProfile) => void;
  onChangeCompany?: (user: UserProfile) => void;
  onSendInvitation: (user: UserProfile) => void;
  onApprove: (userId: number) => void;
  onSafeDelete: (user: UserProfile) => void;
  onViewActivity: (user: UserProfile) => void;
  canImpersonate: boolean;
  onLoginAs: (user: UserProfile) => void;
  isSelf: boolean;
  actionLoading: boolean;
}

function UserTableRow({
  userProfile,
  selected,
  onToggleSelection,
  onViewDetails,
  onToggleActive,
  onChangeRole,
  onChangeCompany,
  onSendInvitation,
  onApprove,
  onSafeDelete,
  canImpersonate,
  onLoginAs,
  isSelf,
  actionLoading,
}: UserTableRowProps) {
  const { formatDateTime } = useFormatters();
  // CR-6085: the backend's CanManageUsers.has_object_permission now applies
  // an admin-tier rule on top of the same-company check. Surface its verdict
  // (can_manage_user from the row serializer) so the UI hides the actions
  // it knows would 403 — Sarah/Amber asked that the controls not appear at
  // all on higher-tier or sibling-tier rows, not just be disabled. Default
  // true keeps pre-CR-6085 behaviour (still server-gated) on any stale row
  // that hasn't picked up the new field yet.
  const canManage = userProfile.can_manage_user !== false;
  const mutatingTooltip = canManage ? undefined : 'You cannot manage an admin whose scope is broader than your own';
  return (
    <TableRow className="hover:bg-muted/50">
      <TableCell>
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelection(userProfile.id)}
        />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {userProfile.user.email}
      </TableCell>
      <TableCell className="font-medium">{userProfile.user.full_name}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm">{userProfile.company_name}</span>
        </div>
      </TableCell>
      <TableCell className="text-sm whitespace-nowrap">
        {departmentLabel(userProfile.department) || <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell>
        <PrimaryContactCell userProfile={userProfile} />
      </TableCell>
      <TableCell>
        <RoleBadges userProfile={userProfile} />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
        {userProfile.user.last_login
          ? formatDateTime(userProfile.user.last_login)
          : '—'}
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          {/* CR-4551 round 4: show "Invite Sent" while the user is still
              an unaccepted placeholder so the Status column matches the
              supplier-detail Users tab card. */}
          {!userProfile.user.is_active ? (
            <Badge variant="secondary" className="text-xs">
              <X className="h-3 w-3 mr-1" />Inactive
            </Badge>
          ) : userProfile.user.invitation_sent && !userProfile.user.has_joined
              && !userProfile.user.last_login ? (
            /* last_login settles it: someone who has signed in has joined,
               whatever has_joined says. That flag was only ever set by
               following an invitation link, so a user who signed in after a
               password reset kept showing "Invite Sent" beside a real Last
               Login. Sign-in now maintains the flag; this keeps rows created
               before that honest without needing a backfill. */
            <Badge variant="outline" className="text-amber-600 border-amber-600 text-xs">
              Invite Sent
            </Badge>
          ) : (
            <Badge variant="default" className="bg-green-500 text-xs">
              <Check className="h-3 w-3 mr-1" />Active
            </Badge>
          )}
          {!userProfile.is_approved && (
            <Badge variant="outline" className="text-amber-600 border-amber-600 text-xs">
              Pending
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <InvitationBadge userProfile={userProfile} />
      </TableCell>
      <TableCell>
        {/* CR-6273: View + Login as stay as visible buttons; the management
            actions (invite / approve / change role / disable / delete) move
            into the three-dot menu per the agreed layout. */}
        <div className="flex items-center justify-end gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5"
            onClick={() => onViewDetails(userProfile)}
            disabled={actionLoading}
          >
            <Eye className="h-4 w-4" />View
          </Button>
          {/* Login as — same eligibility as the user-detail view: an admin who
              may impersonate, not self, an active non-Helix account (Helix is
              also blocked server-side). */}
          {canImpersonate
            && !isSelf
            && userProfile.company_type !== 'helix'
            && userProfile.user.is_active && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5"
              onClick={() => onLoginAs(userProfile)}
              disabled={actionLoading}
            >
              <LogIn className="h-4 w-4" />Login as
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="More actions"
                disabled={actionLoading}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!canManage && (
                <>
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground max-w-[220px] whitespace-normal">
                    {mutatingTooltip}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                </>
              )}
              {/* CR-5314: Send Invitation only for non-active users (resend /
                  not-yet-joined). */}
              {canSendInvitation(userProfile.user) && (
                <DropdownMenuItem
                  onClick={() => onSendInvitation(userProfile)}
                  disabled={actionLoading || !canManage}
                >
                  <Mail className="h-4 w-4 mr-2" />Send Invitation
                </DropdownMenuItem>
              )}
              {!userProfile.is_approved && (
                <DropdownMenuItem
                  onClick={() => onApprove(userProfile.id)}
                  disabled={actionLoading || !canManage}
                >
                  <UserCheck className="h-4 w-4 mr-2 text-green-600" />Approve User
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => onChangeRole(userProfile)}
                disabled={actionLoading || isSelf || !canManage}
              >
                <Shield className="h-4 w-4 mr-2" />Change Role
              </DropdownMenuItem>
              {onChangeCompany && (
                <DropdownMenuItem
                  onClick={() => onChangeCompany(userProfile)}
                  disabled={actionLoading || isSelf}
                >
                  <Building2 className="h-4 w-4 mr-2" />Change Company
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => onToggleActive(userProfile)}
                disabled={actionLoading || isSelf || !canManage}
              >
                <Power className={`h-4 w-4 mr-2 ${userProfile.user.is_active ? 'text-red-500' : 'text-green-500'}`} />
                {userProfile.user.is_active ? 'Disable User' : 'Enable User'}
              </DropdownMenuItem>
              {/* CR-5312: parent confirms then routes to delete vs. deactivate
                  based on whether the user has any login / audit footprint. */}
              <DropdownMenuItem
                onClick={() => onSafeDelete(userProfile)}
                disabled={actionLoading || isSelf || !canManage}
                className="text-red-600 focus:text-red-600"
              >
                <Trash2 className="h-4 w-4 mr-2" />Delete User
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}
